import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { logEvent } from "../../services/evalMode.js";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { analytics, auth, db } from "../../services/firebase.js";
import {
  EDITOR_ONBOARDING_STORAGE_COMPLETED,
  EDITOR_ONBOARDING_STORAGE_DISMISSED,
  EditorOnboardingStepId,
  getEditorOnboardingSteps,
  isStepSatisfiedByContext,
  OnboardingCompletionContext,
} from "./editorOnboardingSteps.tsx";
import {
  resolveEditorFirstRunPath,
  type EditorFirstRunPath,
} from "../OnboardingFlow/onboardingFlowConfig.ts";
import {
  useDrillViewData,
  useDrillDocumentData,
  useFieldEditorData,
  usePlaybackData,
} from "../hooks/useFieldEditorShared.tsx";

/** Stack below app menus/modals (z-50) so users can open the performer roster and modal. */
const PERFORMER_TUTORIAL_BACKDROP_Z = 40;
const PERFORMER_TUTORIAL_CARD_Z = 55;
const PERFORMER_TUTORIAL_HIGHLIGHT_Z = 51;

/** Above keyboard shortcuts (`z-[10000]`) and editor modals (`z-50`). */
const Z_ONBOARDING_BACKDROP = 10050;
const Z_ONBOARDING_CARD = 10052;

function useOnboardingCompletionContext(): OnboardingCompletionContext {
  const { drill, engine } = useDrillViewData();
  const { drillDocumentData } = useDrillDocumentData();
  const { fieldEditorData } = useFieldEditorData();
  const { playbackState } = usePlaybackData();

  const pageCount = drill?.pages?.length ?? 0;
  const performerCount = drill?.performers?.length ?? 0;
  const [selectedCount, setSelectedCount] = useState(0);

  useEffect(() => {
    if (!engine) return;
    const sync = () => setSelectedCount(engine.selectedPerformers.length);
    engine.on("performers-selected", sync);
    sync();
    return () => {
      engine.removeOn("performers-selected", sync);
    };
  }, [engine]);

  return useMemo(
    () => ({
      isPlaying: playbackState.isPlaying,
      pageCount,
      editMode: fieldEditorData.editMode,
      selectedPerformerCount: selectedCount,
      performerCount,
      audioFile: drillDocumentData?.audioFile,
      showAudioModal: fieldEditorData.showAudioTrackModal,
      showExportDialog: fieldEditorData.showExportDialog,
      showPerformerLabelModal: fieldEditorData.showPerformerLabelModal,
    }),
    [
      playbackState.isPlaying,
      pageCount,
      fieldEditorData.editMode,
      fieldEditorData.showAudioTrackModal,
      fieldEditorData.showExportDialog,
      fieldEditorData.showPerformerLabelModal,
      selectedCount,
      performerCount,
      drillDocumentData?.audioFile,
    ]
  );
}

interface EditorOnboardingOverlayProps {
  /** When false, render nothing */
  active: boolean;
  /** Overrides the first "welcome" step copy when set (e.g. role-based onboarding). */
  welcomeCopy?: { title: string; body: string } | null;
}

function isLowerTutorialLayerStep(
  stepId: EditorOnboardingStepId | undefined
): boolean {
  return (
    stepId === "open-performer-roster" || stepId === "add-performers-modal"
  );
}

function pathFromSearchParams(params: URLSearchParams): EditorFirstRunPath {
  return {
    isBeginner:
      params.get("onboarding_experience") === "beginner" ||
      params.get("onboarding_path") === "beginner",
    isLargeEnsemble:
      params.get("onboarding_ensemble") === "large" ||
      params.get("onboarding_path") === "large_ensemble",
  };
}

export default function EditorOnboardingOverlay({
  active,
  welcomeCopy,
}: EditorOnboardingOverlayProps) {
  const ctx = useOnboardingCompletionContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stepIndex, setStepIndex] = useState(0);
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({});
  const cardRef = useRef<HTMLDivElement>(null);
  const advancedStepRef = useRef<EditorOnboardingStepId | null>(null);
  const [profilePath, setProfilePath] = useState<EditorFirstRunPath | null>(
    null
  );

  const urlPath = useMemo(
    () => pathFromSearchParams(searchParams),
    [searchParams]
  );

  // Fallback: fill missing path flags from user onboarding answers
  useEffect(() => {
    if (!active) return;
    if (urlPath.isBeginner && urlPath.isLargeEnsemble) {
      setProfilePath(null);
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (cancelled || !snap.exists()) return;
        const d = snap.data();
        const resolved = resolveEditorFirstRunPath({
          priorExperience:
            d.experience || d.onboardingProfile?.priorExperience,
          typicalPerformerCount:
            d.typicalPerformerCount ||
            d.onboardingProfile?.typicalPerformerCount,
        });
        setProfilePath({
          isBeginner: urlPath.isBeginner ? false : resolved.isBeginner,
          isLargeEnsemble: urlPath.isLargeEnsemble
            ? false
            : resolved.isLargeEnsemble,
        });
      } catch {
        // non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, urlPath.isBeginner, urlPath.isLargeEnsemble]);

  const firstRunPath: EditorFirstRunPath = useMemo(
    () => ({
      isBeginner: urlPath.isBeginner || Boolean(profilePath?.isBeginner),
      isLargeEnsemble:
        urlPath.isLargeEnsemble || Boolean(profilePath?.isLargeEnsemble),
    }),
    [urlPath, profilePath]
  );

  const steps = useMemo(
    () => getEditorOnboardingSteps(firstRunPath),
    [firstRunPath]
  );

  // Reset step index if the path-resolved step list changes length/order
  useEffect(() => {
    setStepIndex(0);
    advancedStepRef.current = null;
  }, [firstRunPath.isBeginner, firstRunPath.isLargeEnsemble]);

  const step = steps[stepIndex];
  const lowerTutorialLayer = isLowerTutorialLayerStep(step?.id);

  const clearTutorialQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tutorial");
        next.delete("onboarding_experience");
        next.delete("onboarding_ensemble");
        next.delete("onboarding_path");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const persistCompleted = useCallback(async () => {
    localStorage.setItem(EDITOR_ONBOARDING_STORAGE_COMPLETED, "true");
    const uid = auth.currentUser?.uid;
    if (uid) {
      try {
        await updateDoc(doc(db, "users", uid), {
          editorOnboardingV2Completed: true,
          editorOnboardingV2CompletedAt: serverTimestamp(),
        });
      } catch {
        // non-blocking
      }
    }
    clearTutorialQuery();
  }, [clearTutorialQuery]);

  const handleDismiss = useCallback(() => {
    logEvent(analytics, "editor_tutorial_skipped", {
      step_id: step?.id ?? "unknown",
    });
    localStorage.setItem(EDITOR_ONBOARDING_STORAGE_DISMISSED, "true");
    clearTutorialQuery();
  }, [clearTutorialQuery, step?.id]);

  const finishTour = useCallback(() => {
    logEvent(analytics, "editor_tutorial_finished", {});
    void persistCompleted();
  }, [persistCompleted]);

  const handleNext = useCallback(() => {
    if (!step) return;
    logEvent(analytics, "editor_tutorial_step_completed", {
      step_id: step.id,
      via: "next_button",
    });
    if (step.id === "done") {
      finishTour();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [finishTour, step, steps.length]);

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Auto-advance when interactive step condition is met
  useEffect(() => {
    if (!active || !step) return;
    if (
      step.id === "welcome" ||
      step.id === "done" ||
      step.id === "free-limit-warning"
    ) {
      return;
    }
    if (!isStepSatisfiedByContext(step.id, ctx)) return;
    if (advancedStepRef.current === step.id) return;
    advancedStepRef.current = step.id;
    logEvent(analytics, "editor_tutorial_step_completed", {
      step_id: step.id,
      via: "action",
    });
    const t = window.setTimeout(() => {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }, 450);
    return () => window.clearTimeout(t);
  }, [active, step, ctx, steps.length]);

  useEffect(() => {
    advancedStepRef.current = null;
  }, [stepIndex]);

  const startedLoggedRef = useRef(false);
  useEffect(() => {
    if (!active || startedLoggedRef.current) return;
    startedLoggedRef.current = true;
    logEvent(analytics, "editor_tutorial_started", {
      is_beginner: firstRunPath.isBeginner,
      is_large_ensemble: firstRunPath.isLargeEnsemble,
    });
  }, [active, firstRunPath.isBeginner, firstRunPath.isLargeEnsemble]);

  // Highlight target + position card
  useLayoutEffect(() => {
    if (!active || !step) return;

    const highlightZ = lowerTutorialLayer
      ? PERFORMER_TUTORIAL_HIGHLIGHT_Z
      : 10051;

    const highlight = (el: Element | null) => {
      document
        .querySelectorAll("[data-onboarding-highlight]")
        .forEach((n) => {
          n.removeAttribute("data-onboarding-highlight");
          n.classList.remove(
            "ring-4",
            "ring-blue-500",
            "ring-offset-2",
            "relative"
          );
          if (n instanceof HTMLElement) {
            n.style.removeProperty("z-index");
          }
        });
      if (el instanceof HTMLElement) {
        el.setAttribute("data-onboarding-highlight", "true");
        el.classList.add(
          "relative",
          "ring-4",
          "ring-blue-500",
          "ring-offset-2"
        );
        el.style.zIndex = String(highlightZ);
      }
    };

    if (!step.targetTour) {
      highlight(null);
      setCardStyle({
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(420px, calc(100vw - 32px))",
      });
      return;
    }

    const el = document.querySelector(`[data-tour="${step.targetTour}"]`);
    highlight(el);

    const margin = 12;
    const gap = 12;
    const maxCardWidth = Math.min(400, window.innerWidth - 2 * margin);
    const maxHeightCss = `min(50vh, 360px, calc(100vh - ${2 * margin}px))`;

    const place = () => {
      const target = document.querySelector(
        `[data-tour="${step.targetTour}"]`
      );
      if (!target) {
        setCardStyle({
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(420px, calc(100vw - 32px))",
        });
        return;
      }

      const r = target.getBoundingClientRect();
      let left = r.left + r.width / 2 - maxCardWidth / 2;
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - maxCardWidth - margin)
      );

      if (step.targetTour === "performer-modal-add") {
        left = margin;
      }

      const cardEl = cardRef.current;
      const measuredH = cardEl?.getBoundingClientRect().height;
      const fallbackH = Math.min(window.innerHeight * 0.45, 320) + 100;
      const cardH = measuredH && measuredH > 0 ? measuredH : fallbackH;

      const spaceBelow = window.innerHeight - margin - (r.bottom + gap);
      const spaceAbove = r.top - margin - gap;

      let top = r.bottom + gap;
      if (step.targetTour === "performer-modal-add") {
        top = Math.max(
          margin,
          Math.min(
            r.top,
            window.innerHeight - margin - cardH
          )
        );
      } else if (cardH <= spaceBelow) {
        top = r.bottom + gap;
      } else if (cardH <= spaceAbove) {
        top = r.top - gap - cardH;
      } else {
        top = margin;
      }

      if (top + cardH > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - margin - cardH);
      }
      if (top < margin) {
        top = margin;
      }

      setCardStyle({
        position: "fixed",
        left,
        top,
        width: maxCardWidth,
        maxHeight: maxHeightCss,
        overflowY: "auto",
        transform: "none",
      });
    };

    place();
    let rafOuter = 0;
    let rafInner = 0;
    rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(place);
    });
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      highlight(null);
    };
  }, [active, step, lowerTutorialLayer]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, handleDismiss]);

  if (!active || !step) return null;

  const displayTitle =
    step.id === "welcome" && welcomeCopy && !firstRunPath.isBeginner
      ? welcomeCopy.title
      : step.title;
  const displayBody =
    step.id === "welcome" && welcomeCopy && !firstRunPath.isBeginner
      ? welcomeCopy.body
      : step.body;

  const backdropZ = lowerTutorialLayer
    ? PERFORMER_TUTORIAL_BACKDROP_Z
    : Z_ONBOARDING_BACKDROP;
  const cardZ = lowerTutorialLayer
    ? PERFORMER_TUTORIAL_CARD_Z
    : Z_ONBOARDING_CARD;

  return (
    <>
      <div
        className="fixed inset-0 pointer-events-none bg-black/20"
        style={{ zIndex: backdropZ }}
        aria-hidden
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-onboarding-title"
        aria-live="polite"
        className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 p-5 text-left"
        style={{ ...cardStyle, zIndex: cardZ }}
      >
        <div className="flex justify-between items-start gap-3 mb-3">
          <h2
            id="editor-onboarding-title"
            className="text-lg font-semibold text-gray-900"
          >
            {displayTitle}
          </h2>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Dismiss tutorial"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{displayBody}</p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-400">
            {stepIndex + 1} / {steps.length}
          </span>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleDismiss}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {step.id === "done" ? "Done" : "Next"}
              {step.id !== "done" && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">Press Esc to dismiss.</p>
      </div>
    </>
  );
}
